import { categories as categoryNames, type AppDetail, type Locale } from '@appwin/shared';
import { CATALOG_BASE_URL, localePath, OTHER_LOCALE } from '../config.js';
import { htmlLang, translator } from '../i18n.js';
import { Button, Page } from '@appwin/ui';
import { absoluteUrl, type HeadInput } from '../head.js';

export interface AppPageProps {
  locale: Locale;
  app: AppDetail;
}

/** Page body for an app. Head tags are produced by `buildAppHead`. */
export function AppPage({ locale, app }: AppPageProps) {
  const t = translator(locale);
  const other = OTHER_LOCALE[locale];
  const installDeepLink = `appwin://install?appId=${encodeURIComponent(app.id)}`;

  // Direct download always points at the publisher's own file: Appwin does not
  // mirror installers, and the catalogue should not pretend otherwise.
  const latestRelease = app.releases
    .filter((release) => release.status === 'published' && release.downloadUrl)
    .sort((a, b) => (b.publishedAt ?? 0) - (a.publishedAt ?? 0))[0];
  const downloadUrl = latestRelease?.downloadUrl ?? '#';
  const versionCount = app.releases.filter((release) => release.status === 'published').length;

  return (
    <Page>
      <header className="flex items-center justify-between gap-4 py-5">
        <a href={localePath(locale, '/')} className="text-sm text-muted hover:text-fg">
          ← {t('common.appName')}
        </a>
        <nav className="flex items-center gap-2 text-sm">
          <a
            href={localePath(other, `/apps/${app.id}`)}
            className="text-muted hover:text-fg"
            lang={htmlLang(other)}
          >
            {other === 'ru' ? 'Русский' : 'English'}
          </a>
        </nav>
      </header>

      <main className="pb-16">
        <div className="flex flex-col gap-6 rounded-2xl border border-border bg-surface p-6 sm:flex-row sm:items-start">
          <div className="flex-1">
            <h1 className="text-2xl font-semibold">{app.name}</h1>
            <p className="mt-1 text-muted">
              {app.developerName ?? (app.origin === 'winget' ? 'winget' : t('common.unknown'))}
            </p>
            <p className="mt-4 max-w-prose text-sm leading-relaxed">{app.description}</p>

            <dl className="mt-6 grid grid-cols-2 gap-4 text-sm sm:grid-cols-3">
              <Meta
                label={t('app.category')}
                value={categoryNames.categoryName(app.category, locale)}
              />
              <Meta label={t('app.license')} value={app.license ?? t('common.unknown')} />
              <Meta label={t('common.version')} value={app.latestVersion ?? t('common.unknown')} />
            </dl>
          </div>

          <aside className="w-full shrink-0 rounded-xl border border-border bg-bg p-4 sm:w-72">
            <a
              href={installDeepLink}
              className="inline-flex h-10 w-full items-center justify-center rounded-xl bg-accent text-sm font-medium text-accent-fg hover:opacity-90"
            >
              {t('web.openInAppwin')}
            </a>
            <p className="mt-2 text-xs text-muted">{t('web.openInAppwinHint')}</p>

            <a
              href={downloadUrl}
              className="mt-3 inline-flex h-10 w-full items-center justify-center rounded-xl border border-border text-sm font-medium hover:border-muted"
              rel="noreferrer noopener"
            >
              {t('web.directDownload')}
            </a>
            <p className="mt-2 text-xs text-muted">{t('web.directDownloadHint')}</p>

            {versionCount > 0 ? (
              <p className="mt-3 text-xs text-muted">
                {t('web.versionsAvailable', { count: versionCount })}
              </p>
            ) : null}
            <div className="mt-4 rounded-lg border border-border p-3">
              <p className="text-sm font-medium">{t('web.getAppwinFirst')}</p>
              <p className="mt-1 text-xs text-muted">{t('web.getAppwinFirstHint')}</p>
              <div className="mt-3">
                <a href={localePath(locale, '/download')}>
                  <Button variant="secondary" size="sm" className="w-full">
                    {t('web.downloadClient')}
                  </Button>
                </a>
              </div>
            </div>
          </aside>
        </div>

      </main>
    </Page>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-muted">{label}</dt>
      <dd className="mt-0.5">{value}</dd>
    </div>
  );
}

/** Head tags and JSON-LD for an app page. */
export function buildAppHead(locale: Locale, app: AppDetail): HeadInput {
  const t = translator(locale);
  return {
    title: `${app.name} — ${t('common.appName')}`,
    description: app.summary,
    canonicalPath: localePath(locale, `/apps/${app.id}`),
    imagePath: `/og/${app.id}.png`,
    locale,
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: app.name,
      description: app.summary,
      applicationCategory: app.category,
      operatingSystem: 'Windows',
      softwareVersion: app.latestVersion ?? undefined,
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
      url: absoluteUrl(localePath(locale, `/apps/${app.id}`)),
      publisher: app.developerName
        ? { '@type': 'Organization', name: app.developerName }
        : undefined,
    },
  };
}

export { CATALOG_BASE_URL };
